import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { GuidedTour } from "@/components/GuidedTour";
import { Suspense, useEffect, useState } from "react";
import { PageLoader, PageErrorBoundary } from './LoadingFeedback';
import { useAuth } from "@/context/AuthContext";
import { useTheme, FONT_STEPS } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Gem, Layers, ShoppingCart, Tag, Tags, Users, FileBarChart, Sparkles, PieChart, History, UserCog, Hammer, Cog, Boxes, Gavel, Camera, FileSpreadsheet, Banknote, ClipboardList, Truck, PackageCheck, Activity, Images, FileUp, Coins, ScanLine, FolderOpen, RotateCcw, MailQuestion, WalletCards, PackagePlus } from "lucide-react";
import { CatawikiIcon } from "@/components/CatawikiIcon";
import BackToTop from "@/components/BackToTop";
import { AdvisorPanel } from "@/components/AdvisorPanel";
import HybridSidebar from "@/components/HybridSidebar";
import CompactTopBar from "@/components/CompactTopBar";
import { pageMetaFor } from "@/lib/pageMeta";

const SIDEBAR_KEY = "carat-ledger-sidebar-collapsed";

// Grouped navigation — clearer information architecture.
const NAV_GROUPS = [
  { title: null, icon: LayoutDashboard, items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  ]},
  { title: "Inventory", icon: Boxes, items: [
    { to: "/stock-reports", label: "Current Stock", icon: FileBarChart, testid: "nav-stock-reports", perm: "workshop" },
    { to: "/certified", label: "Certified", icon: Gem, testid: "nav-certified" },
    { to: "/certified/summary", label: "Cert Summary", icon: PieChart, testid: "nav-cert-summary" },
    { to: "/loose", label: "Loose Parcels", icon: Layers, testid: "nav-loose" },
    { to: "/jewelry-stock", label: "Jewelry Stock", icon: Boxes, testid: "nav-jewelry-stock" },
    { to: "/jewelry-sets", label: "Jewelry Sets", icon: PackagePlus, testid: "nav-jewelry-sets" },
    { to: "/item-names", label: "Item Names", icon: Tags, testid: "nav-item-names" },
    { to: "/stocktake", label: "Stock-take", icon: ScanLine, testid: "nav-stocktake", perm: "workshop" },
  ]},
  { title: "Workshop", icon: Hammer, items: [
    { to: "/production", label: "Production", icon: Hammer, testid: "nav-production", perm: "workshop" },
    { to: "/jewelry", label: "Create Jewelry", icon: Sparkles, testid: "nav-jewelry", perm: "workshop" },
    { to: "/jewelry-import", label: "Import Jewelry", icon: FileUp, testid: "nav-jewelry-import", perm: "workshop" },
    { to: "/gold-stock", label: "Gold Stock", icon: Coins, testid: "nav-gold-stock", perm: "workshop" },
    { to: "/jewelry-library", label: "Image Library", icon: Images, testid: "nav-jewelry-library", perm: "workshop" },
  ]},
  { title: "Purchasing", icon: ShoppingCart, items: [
    { to: "/purchases", label: "Purchases", icon: ShoppingCart, testid: "nav-purchases", perm: "purchases" },
    { to: "/supplier-returns", label: "Supplier Returns", icon: RotateCcw, testid: "nav-supplier-returns", perm: "purchases" },
    { to: "/reorder", label: "Reorder", icon: RotateCcw, testid: "nav-reorder", perm: "purchases" },
  ]},
  { title: "Sales", icon: Tag, items: [
    { to: "/sales", label: "Sales", icon: Tag, testid: "nav-sales", perm: "sales" },
    { to: "/sales-agents", label: "Sales Agent", icon: Users, testid: "nav-sales-agents", perm: "sales" },
    { to: "/inquiries", label: "Inquiries", icon: MailQuestion, testid: "nav-inquiries", perm: "sales" },
    { to: "/sold", label: "Sold", icon: PackageCheck, testid: "nav-sold", perm: "sales" },
  ]},
  { title: "Catawiki", icon: CatawikiIcon, logo: CatawikiIcon, items: [
    { to: "/catawiki/baskets", label: "Prep Baskets", icon: Gavel, testid: "nav-catawiki-baskets", perm: "catawiki" },
    { to: "/catawiki/photography", label: "Photography", icon: Camera, testid: "nav-catawiki-photography", perm: "catawiki" },
    { to: "/catawiki/listing", label: "Listing", icon: FileSpreadsheet, testid: "nav-catawiki-listing", perm: "catawiki" },
    { to: "/catawiki/submitted", label: "Catawiki Stock", icon: ClipboardList, testid: "nav-catawiki-submitted", perm: "catawiki" },
    { to: "/catawiki/shipping", label: "Shipping", icon: Truck, testid: "nav-catawiki-shipping", perm: "catawiki" },
    { to: "/catawiki/payments", label: "Payments & Results", icon: Banknote, testid: "nav-catawiki-payments", perm: "accounting" },
    { to: "/catawiki/profit", label: "Profit & VAT", icon: PieChart, testid: "nav-catawiki-profit", perm: "accounting" },
  ]},
  { title: "Business", icon: FileBarChart, items: [
    { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
    { to: "/sales?view=balances&origin=menu", label: "Payments & Balances", icon: WalletCards, testid: "nav-payments-balances", perm: "accounting" },
    { to: "/reports", label: "Business Reports", icon: FileBarChart, testid: "nav-reports", perm: "accounting" },
    { to: "/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents", perm: "catawiki" },
    { to: "/audit", label: "Audit Log", icon: History, testid: "nav-audit" },
  ]},
];

const ADMIN_GROUP = { title: "Admin", icon: Cog, items: [
  { to: "/settings", label: "Settings", icon: Cog, testid: "nav-settings" },
  { to: "/team", label: "Team", icon: UserCog, testid: "nav-team" },
  { to: "/login-activity", label: "Login Activity", icon: Activity, testid: "nav-login-activity" },
]};

export default function AppLayout() {
  const { user, logout, can } = useAuth();
  const { theme, toggle: toggleTheme, setTheme, accent, setAccent, fontScale, setFontScale } = useTheme();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const pageMeta = pageMetaFor(pathname, search);

  // Sync server-saved preferences once the user is known (cross-device persistence).
  useEffect(() => {
    if (!user) return;
    if (user.accent !== undefined && user.accent !== accent) setAccent(user.accent || "");
    if (user.theme && user.theme !== theme) setTheme(user.theme);
    if (user.font_scale && user.font_scale !== fontScale) setFontScale(user.font_scale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const persistPrefs = (prefs) => {
    api.patch("/auth/preferences", prefs).catch((e) => console.error("Failed to save preferences", e));
  };
  const handleAccent = (key) => {
    setAccent(key || "");
    persistPrefs({ accent: key || "" });
  };
  const handleToggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    toggleTheme();
    persistPrefs({ theme: next });
  };
  const stepFont = (dir) => {
    const i = FONT_STEPS.indexOf(fontScale);
    const next = FONT_STEPS[Math.min(FONT_STEPS.length - 1, Math.max(0, (i === -1 ? 1 : i) + dir))];
    if (next !== fontScale) { setFontScale(next); persistPrefs({ font_scale: next }); }
  };

  // New price requests badge on the Inquiries nav item.
  const [inqBadge, setInqBadge] = useState(0);
  useEffect(() => {
    const load = () => api.get("/inquiries/badge", { loadingIndicator: false }).then(({ data }) => setInqBadge(data.new || 0)).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [pathname]);

  const isOwner = user?.role === "owner";
  const baseGroups = isOwner ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS;
  // Hide nav items the member lacks permission for; drop any group left empty.
  const groups = baseGroups
    .map((g) => ({ ...g, items: g.items.filter((it) => !it.perm || can(it.perm)) }))
    .filter((g) => g.items.length > 0);

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  });
  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0");
  }, [collapsed]);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="erp-shell min-h-screen flex bg-[#0A0D14] text-slate-100" style={{ "--sidebar-w": collapsed ? "4rem" : "16rem" }}>
      <HybridSidebar groups={groups} pathname={pathname} search={search} collapsed={collapsed} setCollapsed={setCollapsed}
        inqBadge={inqBadge} user={user} navigate={navigate}
        mobileOpen={mobileNavOpen} setMobileOpen={setMobileNavOpen} />
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <CompactTopBar user={user} isOwner={isOwner} logout={logout} navigate={navigate} pathname={pathname}
          theme={theme} handleToggleTheme={handleToggleTheme} accent={accent} handleAccent={handleAccent}
          fontScale={fontScale} stepFont={stepFont} />
        <main data-app-content className={`erp-content flex-1 p-6 md:p-8 overflow-x-clip ${pageMeta.hideContentTitle ? "compact-page-titles" : ""}`}>
          <PageErrorBoundary key={pathname}>
            <Suspense fallback={<PageLoader label={`Loading ${pageMeta.title || 'page'}…`} />}>
              <div className="page-transition"><Outlet /></div>
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
      <BackToTop />
      <AdvisorPanel />
      <GuidedTour />
    </div>
  );
}
